import { useEffect, useMemo, useState } from "react";
import { X, RotateCw, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { rerunCase } from "@/lib/api";

/**
 * Modal shown from the unlocked report. Lets the user tweak any clarifying
 * answer and re-run the engine. Creates a NEW case inheriting unlocked=true.
 */
export default function RerunModal({
  open,
  onClose,
  caseId,
  clarifyingQuestions = [],
  currentAnswers = [],
  onSubmitted,
}) {
  // Build editable state from parent — match by question text. Only include
  // rows that have a prior answer, so legacy cases where not every clarifying
  // question was answered don't force the user to fill blanks they never
  // saw.
  const initial = useMemo(() => {
    return clarifyingQuestions
      .map((q) => {
        const found = currentAnswers.find((a) => a.question === q.question);
        return {
          question: q.question,
          options: q.options || [],
          answer: found?.answer || "",
          _hadPrior: !!found,
        };
      })
      .filter((r) => r._hadPrior);
  }, [clarifyingQuestions, currentAnswers]);

  const [rows, setRows] = useState(initial);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (open) {
      setRows(initial);
      setError(null);
    }
  }, [open, initial]);

  if (!open) return null;

  const dirty = rows.some((r, i) => r.answer !== initial[i]?.answer);
  const complete = rows.every((r) => (r.answer || "").trim().length > 0);

  const setAnswer = (idx, val) => {
    setRows((prev) => {
      const next = [...prev];
      next[idx] = { ...next[idx], answer: val };
      return next;
    });
  };

  const submit = async () => {
    if (!dirty || !complete) return;
    setSubmitting(true);
    setError(null);
    try {
      const res = await rerunCase(
        caseId,
        rows.map((r) => ({ question: r.question, answer: r.answer.trim() }))
      );
      if (onSubmitted) onSubmitted(res.case_id);
    } catch (e) {
      setError(e?.response?.data?.detail || e.message || "Rerun failed.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm"
      onClick={onClose}
      data-testid="rerun-modal"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="relative w-full max-w-2xl max-h-[90vh] rounded-2xl border border-[#35D07F]/40 bg-[#12181A] flex flex-col"
      >
        <button
          onClick={onClose}
          aria-label="Close"
          data-testid="rerun-close"
          className="absolute top-3 right-3 h-8 w-8 inline-flex items-center justify-center rounded-full text-white/50 hover:text-white hover:bg-white/5 z-10"
        >
          <X className="h-4 w-4" />
        </button>

        <div className="p-6 sm:p-8 border-b border-white/10">
          <div className="text-[11px] uppercase tracking-[0.2em] text-[#35D07F]">
            Tweak & rerun
          </div>
          <h3 className="mt-2 text-2xl font-bold tracking-tight text-white">
            Change any answer, run the engine again.
          </h3>
          <p className="mt-2 text-sm text-[#A0AAB2] leading-relaxed">
            We'll spin up a fresh set of 15–20 questions and answered top 5 —
            takes about 90 seconds. Your original report stays put at its URL.
          </p>
        </div>

        <div className="p-6 sm:p-8 space-y-5 overflow-y-auto flex-1">
          {rows.map((row, idx) => {
            const changed = row.answer !== initial[idx]?.answer;
            return (
              <div
                key={idx}
                data-testid={`rerun-row-${idx}`}
                className={`rounded-xl border p-5 ${
                  changed
                    ? "border-[#35D07F]/50 bg-[#35D07F]/[0.04]"
                    : "border-white/10 bg-[#0B0F10]"
                }`}
              >
                <div className="flex items-baseline gap-3">
                  <span className="text-xs font-mono text-[#35D07F]">
                    {String(idx + 1).padStart(2, "0")}
                  </span>
                  <div className="flex-1 text-white font-medium leading-snug text-[15px]">
                    {row.question}
                  </div>
                  {changed && (
                    <span className="text-[10px] uppercase tracking-[0.2em] text-[#35D07F] font-semibold">
                      changed
                    </span>
                  )}
                </div>

                {row.options && row.options.length > 0 ? (
                  <div className="mt-4 flex flex-wrap gap-2">
                    {row.options.map((opt, oi) => {
                      const selected = row.answer === opt;
                      return (
                        <button
                          key={oi}
                          type="button"
                          onClick={() => setAnswer(idx, opt)}
                          data-testid={`rerun-row-${idx}-option-${oi}`}
                          className={`rounded-full px-4 py-2 text-sm border transition-[background-color,border-color,color] duration-150 ${
                            selected
                              ? "bg-[#35D07F] text-black border-[#35D07F]"
                              : "bg-transparent text-white/90 border-white/15 hover:border-white/40"
                          }`}
                        >
                          {opt}
                        </button>
                      );
                    })}
                    <div className="basis-full h-0" />
                    <Input
                      value={row.answer}
                      onChange={(e) => setAnswer(idx, e.target.value)}
                      placeholder="…or type your own"
                      data-testid={`rerun-row-${idx}-custom`}
                      className="mt-1 bg-[#0B0F10] border-white/10 text-white placeholder:text-white/30 focus-visible:ring-[#35D07F]/50"
                    />
                  </div>
                ) : (
                  <Input
                    value={row.answer}
                    onChange={(e) => setAnswer(idx, e.target.value)}
                    placeholder="Type your answer"
                    data-testid={`rerun-row-${idx}-input`}
                    className="mt-4 bg-[#0B0F10] border-white/10 text-white placeholder:text-white/30 focus-visible:ring-[#35D07F]/50"
                  />
                )}
              </div>
            );
          })}

          {rows.length === 0 && (
            <div className="text-sm text-white/50">
              We don't have the clarifying questions for this case on file.
            </div>
          )}
        </div>

        <div className="p-6 sm:p-8 border-t border-white/10 flex items-center gap-4">
          <Button
            onClick={submit}
            disabled={!dirty || !complete || submitting || rows.length === 0}
            data-testid="rerun-submit"
            className="rounded-full bg-[#35D07F] hover:bg-[#2CBB70] text-black font-semibold h-11 px-6 disabled:opacity-40"
          >
            {submitting ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
                Starting rerun…
              </>
            ) : (
              <>
                <RotateCw className="h-4 w-4 mr-2" />
                Rerun the engine
              </>
            )}
          </Button>
          {!dirty && (
            <span className="text-xs text-white/40">
              Change at least one answer to rerun.
            </span>
          )}
          {dirty && !complete && (
            <span className="text-xs text-white/40">
              Every answer must be filled in.
            </span>
          )}
          {error && (
            <span className="text-xs text-[#FF5252]" data-testid="rerun-error">
              {error}
            </span>
          )}
        </div>
      </div>
    </div>
  );
}
